/**
 * Unified badge component - all badges are uniform in size and styling
 * Uses theme variables for consistent theming
 *
 * @param {string} variant - Color variant (e.g., 'active', 'inactive', 'gray', 'primary')
 * @param {string} className - Additional CSS classes (use sparingly to maintain consistency)
 */
export default function Badge({
  children,
  variant = 'default',
  className = ''
}) {
  const variants = {
    // Status variants
    active: 'bg-green-500/20 text-green-400',
    inactive: 'bg-red-500/20 text-red-400',
    warning: '',

    // Role/Type variants with borders
    admin: 'bg-purple-500/20 text-purple-300 border border-purple-400/50',
    user: 'bg-blue-500/20 text-blue-300 border border-blue-400/50',

    // Conditional variants (based on source)
    direct: 'bg-green-500/20 text-green-400',
    role: 'bg-blue-500/20 text-blue-400',

    // Generic variants - use theme variables (these will use getStyle())
    default: '',
    gray: '',
    'gray-muted': 'italic',
    primary: '',
    info: '',
    success: 'bg-green-500/20 text-green-300'
  };

  const variantClass = variants[variant] || variants.default;

  // Convert children to lowercase if it's a string
  const displayContent = typeof children === 'string' ? children.toLowerCase() : children;

  // Style object for theme-based variants
  const getStyle = () => {
    if (['default', 'gray', 'gray-muted', 'primary'].includes(variant)) {
      return {
        backgroundColor: 'var(--color-bg-tertiary)',
        borderColor: 'var(--color-border-default)',
        borderWidth: '1px',
        color: 'var(--color-text-secondary)'
      };
    }
    if (variant === 'warning') {
      return {
        backgroundColor: 'var(--color-bg-tertiary)',
        borderColor: 'var(--color-border-default)',
        borderWidth: '1px',
        color: 'var(--color-warning)'
      };
    }
    if (variant === 'info') {
      return {
        backgroundColor: 'var(--color-bg-tertiary)',
        borderColor: 'var(--color-border-default)',
        borderWidth: '1px',
        color: 'var(--color-info)'
      };
    }
    return {};
  };

  return (
    <span
      className={`rounded-full inline-block font-medium text-xs px-2 py-1 ${variantClass} truncate max-w-full ${className}`}
      style={getStyle()}
    >
      {displayContent}
    </span>
  );
}
